import type { ChatSessionDto, PaginationMeta } from '@/lib/api/sessions.client';
import { refreshedRow } from '@/lib/stores/chat-store/session-dto';
import { sessionToHistory } from '@/lib/stores/session-history';
import type { IChat, IChatHistory } from '@/lib/types/chat';

/**
 * The reader's own chat lists as the chat store holds them: the plain sidebar's
 * and, per agent that was opened, that agent's. The rewrites below are what
 * every change to a row of them goes through, from the store and from the
 * archive actions alike (KAN-1664).
 */
export interface OwnLists {
  chatHistory: IChatHistory[];
  agentChatsById: Record<string, IChatHistory[]>;
}

export type ListRewrite = (list: IChatHistory[]) => IChatHistory[];

export const holdsChat = (list: IChatHistory[], chatId: string): boolean =>
  list.some((chat) => chat.id === chatId);

// Apply ``rewrite`` to every own list that holds ``chatId``: the plain list and
// the per-agent lists, so the agent panel mirrors what happens to a chat in
// the store. A list without the chat keeps its identity, and so does the
// agent map when no agent list holds it — a change to one chat does not
// re-render lists that never showed it.
export const rewriteListsHolding = (
  state: OwnLists,
  chatId: string,
  rewrite: ListRewrite
): OwnLists => {
  const agentLists = Object.entries(state.agentChatsById);
  const anyAgentList = agentLists.some(([, list]) => holdsChat(list, chatId));
  return {
    chatHistory: holdsChat(state.chatHistory, chatId)
      ? rewrite(state.chatHistory)
      : state.chatHistory,
    agentChatsById: anyAgentList
      ? Object.fromEntries(
          agentLists.map(([agentId, list]) => [
            agentId,
            holdsChat(list, chatId) ? rewrite(list) : list,
          ])
        )
      : state.agentChatsById,
  };
};

export const patchRow =
  (chatId: string, patch: Partial<IChatHistory>): ListRewrite =>
  (list) =>
    list.map((chat) => (chat.id === chatId ? { ...chat, ...patch } : chat));

export const withoutRow =
  (chatId: string): ListRewrite =>
  (list) =>
    list.filter((chat) => chat.id !== chatId);

// A new message: the row moves to the top with its activity time, the
// message's own when it is known, else now. Only ever applied to lists that
// already hold the chat — a bump adds no row.
export const bumpRow =
  (chatId: string, at: string = new Date().toISOString()): ListRewrite =>
  (list) => {
    const row = list.find((chat) => chat.id === chatId);
    if (!row) return list;
    const bumped = { ...row, lastMessageAt: at };
    return [bumped, ...list.filter((chat) => chat.id !== chatId)];
  };

// Whether one of the own lists shows the chat with activity older than ``at``.
export const listedAsOlder = (state: OwnLists, chatId: string, at: number): boolean =>
  [state.chatHistory, ...Object.values(state.agentChatsById)].some((list) =>
    list.some((chat) => chat.id === chatId && !(Date.parse(chat.lastMessageAt) >= at))
  );

// ``entry`` refreshed where ``list`` holds it, otherwise put on top if
// ``mayAdd``. Never takes a row out: what a list holds, the server put there.
export const placeRow = (
  list: IChatHistory[],
  entry: IChatHistory,
  mayAdd: boolean
): IChatHistory[] => {
  if (holdsChat(list, entry.id)) {
    return list.map((chat) => (chat.id === entry.id ? refreshedRow(chat, entry) : chat));
  }
  return mayAdd ? [entry, ...list] : list;
};

// Into the list of the entry's agent, where that list is loaded. One that is
// not stays unloaded: a single chat is no reason to materialise a partial list.
const placeInAgentList = (
  agentChatsById: Record<string, IChatHistory[]>,
  entry: IChatHistory,
  mayAdd: boolean
): Record<string, IChatHistory[]> => {
  const list = entry.agentId ? agentChatsById[entry.agentId] : undefined;
  if (!entry.agentId || !list) return agentChatsById;
  return { ...agentChatsById, [entry.agentId]: placeRow(list, entry, mayAdd) };
};

// The reader's own lists with a chat they just opened or created.
//
// ``chatHistory`` is the plain area exactly as the server lists it: the
// reader's own chats outside agents and projects, plus own chats whose agent
// or project they can no longer open — those carry ``agentId`` or
// ``projectId`` and still belong there. So a listed row is refreshed where it
// is and never taken out, and a row is only added for a plain chat. A chat of
// an agent or a project is listed by the server in that agent or project; the
// plain sidebar used to filter agent chats back out because opening one put
// it here.
//
// The agent list gets the chat even when the user reached it from elsewhere
// (the command palette, say). Someone else's chat (``sharedBy``, or
// read-only) is added to neither: it is listed only under "Geteilt mit mir"
// of its area (KAN-1416). Nor is a chat the reader archived, opened from
// "Archiviert" or the search (KAN-1664).
export const ownListsWith = (
  state: OwnLists,
  entry: IChatHistory,
  chat: Pick<IChat, 'readOnly' | 'sharedBy'>
): OwnLists => {
  const own = !chat.readOnly && !chat.sharedBy && !entry.isArchived;
  const plain = own && !entry.agentId && !entry.projectId;
  return {
    chatHistory: placeRow(state.chatHistory, entry, plain),
    agentChatsById: placeInAgentList(state.agentChatsById, entry, own),
  };
};

/** The plain list's paging state, as the chat store holds it. */
interface PlainPage {
  chatHistory: IChatHistory[];
  sessionsOffset: number;
  sessionsHasMore: boolean;
}

// The first page of the plain list, as the store holds it.
export const firstPlainPage = (
  sessions: ChatSessionDto[],
  pagination: PaginationMeta | undefined,
  limit: number
): PlainPage => ({
  chatHistory: sessions.map(sessionToHistory),
  sessionsOffset: pagination ? pagination.offset + pagination.count : sessions.length,
  sessionsHasMore: pagination?.hasMore ?? sessions.length === limit,
});
